import { memo, useCallback, useLayoutEffect, useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { hapticLight } from '@/telegram'
import { usePlaceBet } from '@/hooks/usePlaceBet'
import { useChartApi } from '@/contexts/ChartApiContext'
import { parsePriceRange, type MatrixCell } from '@/lib/rangeMarketUtils'
import { cn } from '@/lib/utils'

const LONG_PRESS_MS = 1000

interface ChartAlignedMatrixProps {
  matrix: MatrixCell[][]
  endDates?: [string | null, string | null, string | null]
  disabled?: boolean
  onCellTap?: (cell: MatrixCell, side?: 'YES' | 'NO') => void
  selectedCellIds?: Set<string>
  onToggleSelection?: (cellId: string) => void
  arbSums?: number[]
  positionOrPriceSums?: number[]
  arbSumsNo?: number[]
  positionOrPriceSumsNo?: number[]
  /** Page-level container rect for portal positioning */
  containerPageRect?: { left: number; top: number }
}

const SECONDS_PER_DAY = 24 * 3600

/** ISO date → unix seconds */
function isoToUnix(iso: string | null | undefined): number | null {
  if (!iso) return null
  const ms = new Date(iso).getTime()
  return isNaN(ms) ? null : Math.floor(ms / 1000)
}

/** Ячейки матрицы, привязанные к координатам графика (цена по Y, 1 день по X) */
export const ChartAlignedMatrix = memo(function ChartAlignedMatrix({
  matrix,
  endDates,
  disabled,
  onCellTap,
  selectedCellIds,
  onToggleSelection,
  arbSums,
  positionOrPriceSums,
  arbSumsNo,
  positionOrPriceSumsNo,
}: ChartAlignedMatrixProps) {
  const { chart, series } = useChartApi()
  const placeBet = usePlaceBet()
  const [cellBounds, setCellBounds] = useState<Map<string, { left: number; top: number; width: number; height: number }>>(new Map())
  const [colPxState, setColPxState] = useState<{ left: number; right: number }[]>([])
  const [lineLeftPx, setLineLeftPx] = useState<number | null>(null)
  const [gridLines, setGridLines] = useState<{ verticals: number[]; horizontals: number[] }>({ verticals: [], horizontals: [] })
  const [resizeVersion, setResizeVersion] = useState(0)
  const [containerRect, setContainerRect] = useState<{ left: number; top: number } | null>(null)

  useLayoutEffect(() => {
    if (!chart || !series || !matrix.length) {
      setCellBounds(new Map())
      setLineLeftPx(null)
      return
    }

    const timeScale = chart.timeScale()
    const priceScale = chart.priceScale('left')
    const priceScaleWidth = priceScale.width()

    // Видимый временной диапазон графика (data range)
    let visibleRange: ReturnType<typeof timeScale.getVisibleRange> = null
    try {
      visibleRange = timeScale.getVisibleRange()
    } catch {
      setCellBounds(new Map())
      setLineLeftPx(null)
      return
    }
    if (!visibleRange) {
      setCellBounds(new Map())
      setLineLeftPx(null)
      return
    }
    const rangeFrom = visibleRange.from as number
    const rangeTo = visibleRange.to as number
    const rangeSpan = rangeTo - rangeFrom
    if (rangeSpan <= 0) { setCellBounds(new Map()); setLineLeftPx(null); return }

    const CANDLE = 3600
    const snap = (t: number) => Math.round(t / CANDLE) * CANDLE

    // px/sec: пиксельная шкала из двух точек внутри данных
    const tA = snap(rangeFrom)
    const tB = snap(rangeTo)
    const xA = timeScale.timeToCoordinate(tA as import('lightweight-charts').UTCTimestamp)
    const xB = timeScale.timeToCoordinate(tB as import('lightweight-charts').UTCTimestamp)
    if (xA == null || xB == null) { setCellBounds(new Map()); setLineLeftPx(null); return }
    const pxPerSec = (xB - xA) / (tB - tA)

    // Пиксельные координаты через экстраполяцию
    const timeToPx = (t: number) => priceScaleWidth + xA + (t - tA) * pxPerSec

    // Границы колонок — от времени ресолва первой колонки (или видимого диапазона графика), сетка по 1 дню
    const ed0 = isoToUnix(endDates?.[0])
    const anchor = ed0 ?? rangeTo // конец колонки 0 (ресолв или правый край графика)
    const todayStart = anchor - SECONDS_PER_DAY
    const col0End = anchor
    const col1End = anchor + SECONDS_PER_DAY
    const col2End = anchor + 2 * SECONDS_PER_DAY

    const colPx = [
      { left: timeToPx(todayStart), right: timeToPx(col0End) },
      { left: timeToPx(col0End), right: timeToPx(col1End) },
      { left: timeToPx(col1End), right: timeToPx(col2End) },
    ]

    const bounds = new Map<string, { left: number; top: number; width: number; height: number }>()

    // Средняя высота рядов с обеими границами (для верхнего/нижнего с открытым диапазоном)
    const middleHeights: number[] = []
    for (let ri = 0; ri < matrix.length; ri++) {
      const cell = matrix[ri]?.[0]
      const range = cell ? parsePriceRange(cell.rangeLabel) : null
      if (!range || range.high === Infinity || range.low === 0) continue
      const yT = series.priceToCoordinate(range.high)
      const yB = series.priceToCoordinate(range.low)
      if (yT == null || yB == null) continue
      middleHeights.push(Math.abs(yB - yT))
    }
    const avgHeight = middleHeights.length > 0 ? middleHeights.reduce((a, b) => a + b, 0) / middleHeights.length : 60

    for (let ri = 0; ri < matrix.length; ri++) {
      const row = matrix[ri]
      const cell = row[0]
      if (!cell) continue

      const range = parsePriceRange(cell.rangeLabel)
      if (!range) continue

      let top: number
      let height: number

      if (range.high === Infinity) {
        // Верхний ряд (>X): фиксированная высота, привязан к low (нижняя граница)
        const yBottom = series.priceToCoordinate(range.low)
        if (yBottom == null) continue
        top = yBottom - avgHeight
        height = avgHeight
      } else if (range.low === 0) {
        // Нижний ряд (<X): фиксированная высота, привязан к high (верхняя граница)
        const yTop = series.priceToCoordinate(range.high)
        if (yTop == null) continue
        top = yTop
        height = avgHeight
      } else {
        // Средние ряды: по реальным границам
        const yTop = series.priceToCoordinate(range.high)
        const yBottom = series.priceToCoordinate(range.low)
        if (yTop == null || yBottom == null) continue
        top = Math.min(yTop, yBottom)
        height = Math.abs(yBottom - yTop)
      }

      if (height < 4) continue

      const nowSec = Math.floor(Date.now() / 1000)
      for (let ci = 0; ci < row.length; ci++) {
        const edSec = isoToUnix(endDates?.[ci])
        if (ci > 0 && (!edSec || edSec < nowSec)) continue
        const col = colPx[ci]
        if (!col) continue
        const left = col.left
        const width = col.right - col.left
        if (width < 4) continue

        bounds.set(`${ri}-${ci}`, { left, top, width, height })
      }
    }

    setCellBounds(bounds)
    setColPxState(colPx)
    // Линия «сейчас» отключена — не рисуем вертикальную линию времени
    setLineLeftPx(null)

    // Сетка: вертикали по 12:00 ET каждого дня — смещение берём из endDate[0] (DST-aware)
    const vLines: number[] = []
    const ed0Sec = isoToUnix(endDates?.[0])
    const noonOffsetSec = ed0Sec != null
      ? ed0Sec % SECONDS_PER_DAY   // 16*3600 при EDT, 17*3600 при EST
      : 17 * 3600                   // fallback EST
    let noonTime = Math.floor(rangeFrom / SECONDS_PER_DAY) * SECONDS_PER_DAY + noonOffsetSec
    if (noonTime < rangeFrom) noonTime += SECONDS_PER_DAY
    while (noonTime <= rangeTo) {
      vLines.push(timeToPx(noonTime))
      noonTime += SECONDS_PER_DAY
    }
    const hSet = new Set<number>()
    for (let ri = 0; ri < matrix.length; ri++) {
      const cell = matrix[ri][0]
      if (!cell) continue
      const range = parsePriceRange(cell.rangeLabel)
      if (!range) continue
      if (range.high === Infinity) {
        const yB = series.priceToCoordinate(range.low)
        if (yB != null) {
          hSet.add(yB - avgHeight)
          hSet.add(yB)
        }
      } else if (range.low === 0) {
        const yT = series.priceToCoordinate(range.high)
        if (yT != null) {
          hSet.add(yT)
          hSet.add(yT + avgHeight)
        }
      } else {
        const yT = series.priceToCoordinate(range.high)
        const yB = series.priceToCoordinate(range.low)
        if (yT != null) hSet.add(Math.min(yT, yB!))
        if (yB != null) hSet.add(Math.max(yT!, yB))
      }
    }
    setGridLines({ verticals: vLines, horizontals: [...hSet].sort((a, b) => a - b) })

  }, [chart, series, matrix, endDates, resizeVersion])

  // RAF-троттлинг: макс 1 setState за кадр (≈60 fps вместо сотен при быстром скролле/зуме)
  const resizeRafRef = useRef<number | undefined>(undefined)
  const scheduleResize = useCallback(() => {
    if (resizeRafRef.current != null) return
    resizeRafRef.current = requestAnimationFrame(() => {
      resizeRafRef.current = undefined
      setResizeVersion((v) => v + 1)
    })
  }, [])

  const containerRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => {
      const r = el.getBoundingClientRect()
      setContainerRect({ left: r.left, top: r.top })
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [resizeVersion, gridLines.verticals.length])

  useEffect(() => {
    if (!chart) return
    chart.timeScale().subscribeVisibleLogicalRangeChange(scheduleResize)

    // Опрос ценовой шкалы: при вертикальном зуме subscribeVisibleLogicalRangeChange не срабатывает
    const priceScale = chart.priceScale('left')
    let lastPrFrom = -1
    let lastPrTo = -1
    let tickRaf: number
    const tick = () => {
      try {
        const pr = priceScale.getVisibleRange()
        if (pr && (pr.from !== lastPrFrom || pr.to !== lastPrTo)) {
          lastPrFrom = pr.from
          lastPrTo = pr.to
          scheduleResize()
        }
      } catch {
        // chart may be dismounted or not ready
      }
      tickRaf = requestAnimationFrame(tick)
    }
    tickRaf = requestAnimationFrame(tick)

    return () => {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(scheduleResize)
      cancelAnimationFrame(tickRaf)
    }
  }, [chart, scheduleResize])

  const handleTap = useCallback(
    (cell: MatrixCell, side: 'YES' | 'NO') => {
      if (disabled || placeBet.isPending) return
      if (side === 'YES' && onCellTap) {
        hapticLight()
        onCellTap(cell)
        return
      }
      const tokenId = side === 'YES' ? cell.yesTokenId : cell.noTokenId
      if (!tokenId) return
      hapticLight()
      placeBet.mutate({
        tokenId,
        side,
        amountUsd: 5,
        tickSize: cell.tickSize,
        negRisk: cell.negRisk,
        yesPrice: cell.yesPrice,
        noPrice: cell.noPrice,
      })
    },
    [disabled, placeBet, onCellTap]
  )

  // Tap на ячейке: все элементы pointer-events-none, клик определяем по координатам
  const cellBoundsRef = useRef(cellBounds)
  cellBoundsRef.current = cellBounds
  const matrixRef = useRef(matrix)
  matrixRef.current = matrix
  const handleTapRef = useRef(handleTap)
  handleTapRef.current = handleTap
  const onToggleSelectionRef = useRef(onToggleSelection)
  onToggleSelectionRef.current = onToggleSelection
  const pointerStartRef = useRef({ x: 0, y: 0 })
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressFiredRef = useRef(false)

  const findCellAtPoint = useCallback((clientX: number, clientY: number) => {
    const container = containerRef.current
    if (!container) return null
    const rect = container.getBoundingClientRect()
    const x = clientX - rect.left
    const y = clientY - rect.top
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return null
    const bounds = cellBoundsRef.current
    const mat = matrixRef.current
    for (const [key, b] of bounds) {
      const bW = Math.max(20, b.width)
      const bH = Math.max(12, b.height)
      const bL = b.left
      const bT = b.top
      if (x >= bL && x <= bL + bW && y >= bT && y <= bT + bH) {
        const [ri, ci] = key.split('-').map(Number)
        return mat[ri]?.[ci] ?? null
      }
    }
    return null
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const DRAG_THRESHOLD = 6

    const clearTimer = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
    }

    const onDown = (e: PointerEvent) => {
      longPressFiredRef.current = false
      pointerStartRef.current = { x: e.clientX, y: e.clientY }
      clearTimer()

      const sx = e.clientX, sy = e.clientY
      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null
        const cell = findCellAtPoint(sx, sy)
        if (cell) {
          longPressFiredRef.current = true
          onToggleSelectionRef.current?.(cell.id)
        }
      }, LONG_PRESS_MS)
    }

    const onMove = (e: PointerEvent) => {
      const dx = Math.abs(e.clientX - pointerStartRef.current.x)
      const dy = Math.abs(e.clientY - pointerStartRef.current.y)
      if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) clearTimer()
    }

    const onUp = (e: PointerEvent) => {
      clearTimer()
      if (longPressFiredRef.current) {
        longPressFiredRef.current = false
        return
      }
      const dx = Math.abs(e.clientX - pointerStartRef.current.x)
      const dy = Math.abs(e.clientY - pointerStartRef.current.y)
      if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) return
      const target = e.target as HTMLElement | null
      if (target?.closest('[role="dialog"]')) return
      const cell = findCellAtPoint(e.clientX, e.clientY)
      if (cell) handleTapRef.current(cell, 'YES')
    }

    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('pointermove', onMove, true)
    document.addEventListener('pointerup', onUp, true)
    document.addEventListener('pointercancel', clearTimer, true)
    return () => {
      clearTimer()
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('pointermove', onMove, true)
      document.removeEventListener('pointerup', onUp, true)
      document.removeEventListener('pointercancel', clearTimer, true)
    }
  }, [findCellAtPoint])

  if (!matrix.length) return null

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden pointer-events-none">
      {(arbSums || positionOrPriceSums || arbSumsNo || positionOrPriceSumsNo) && colPxState.length > 0 && colPxState.map((col, i) => {
        const arb = arbSums?.[i] ?? 0
        const pop = positionOrPriceSums?.[i] ?? 0
        const arbNo = arbSumsNo?.[i] ?? 0
        const popNo = positionOrPriceSumsNo?.[i] ?? 0
        if (arb === 0 && pop === 0 && arbNo === 0 && popNo === 0) return null
        const blockWidth = Math.max(45, col.right - col.left)
        const blockLeft = col.left + (col.right - col.left) / 2 - blockWidth / 2
        const sumClass = 'text-xs font-semibold tabular-nums leading-tight'
        return (
          <div
            key={`arb-block-${i}`}
            className="pointer-events-none absolute flex flex-col items-center justify-center gap-0.5 rounded px-1.5 py-1 bg-[#0a0a0a]"
            style={{
              left: blockLeft,
              top: 2,
              width: blockWidth,
              zIndex: 20,
            }}
          >
            <div className="flex justify-center items-center gap-1">
              <span
                className={sumClass}
                style={{
                  color: arb * 100 > 100 ? '#ef4444' : arb * 100 < 100 ? '#22c55e' : '#ffffff',
                  textShadow: '0 0 4px rgba(0,0,0,0.8)',
                }}
              >
                {arb > 0 ? (arb * 100).toFixed(1) : '—'}
              </span>
              <span
                className={`${sumClass} text-red-400`}
                style={{ textShadow: '0 0 4px rgba(0,0,0,0.8)' }}
              >
                {arbNo > 0 ? (arbNo * 100).toFixed(1) : '—'}
              </span>
            </div>
            <div className="flex justify-center items-center gap-1">
              <span
                className={`${sumClass} text-[#94a3b8]`}
                style={{ textShadow: '0 0 4px rgba(0,0,0,0.8)' }}
              >
                {pop > 0 ? (pop * 100).toFixed(1) : '—'}
              </span>
              <span
                className={`${sumClass} text-gray-400`}
                style={{ textShadow: '0 0 4px rgba(0,0,0,0.8)' }}
              >
                {popNo > 0 ? (popNo * 100).toFixed(1) : '—'}
              </span>
            </div>
          </div>
        )
      })}
      {createPortal(
        <div className="pointer-events-none fixed inset-0 z-[5]" aria-hidden>
          {gridLines.verticals.map((x, i) => (
            <div
              key={`gv-${i}`}
              className="absolute top-0 w-px min-h-dvh"
              style={{
                left: (containerRect?.left ?? 0) + x,
                backgroundColor: 'rgba(148,163,184,0.1)',
              }}
            />
          ))}
        </div>,
        document.body
      )}
      {lineLeftPx != null && (
        <div
          className="pointer-events-none absolute top-0 bottom-0 z-10"
          style={{ left: lineLeftPx, width: '1px', backgroundColor: '#64748b' }}
          aria-hidden
        />
      )}
      {matrix.flatMap((row, ri) =>
        row.map((cell, ci) => {
          const b = cellBounds.get(`${ri}-${ci}`)
          if (!b || b.width < 12 || b.height < 12) return null

          const hasOrder = !!cell.orderInfo || !!cell.orderInfoNo
          const hasPosition = !!cell.positionInfo
          const hasPositionNo = !!cell.positionInfoNo
          const overlayStyle = hasOrder
            ? { background: 'linear-gradient(180deg, rgba(245,158,11,0.25) 0%, rgba(245,158,11,0.08) 100%)' }
            : hasPosition
              ? { background: 'linear-gradient(180deg, rgba(0,255,106,0.45) 0%, rgba(0,255,106,0.25) 100%)' }
              : hasPositionNo
                ? { background: 'linear-gradient(180deg, rgba(239,68,68,0.45) 0%, rgba(239,68,68,0.25) 100%)' }
                : { background: 'rgba(107,114,128,0.15)' }

          const btnW = Math.max(20, b.width)
          const btnH = Math.max(12, b.height)
          const btnLeft = b.left
          const btnTop = b.top
          const yesPriceStr = cell.yesTokenId ? (cell.yesPrice * 100).toFixed(1) : '—'
          const noPriceStr = cell.noTokenId ? (cell.noPrice * 100).toFixed(1) : '—'
          const showYesPrice = !(cell.orderInfoNo || cell.positionInfoNo)
          const showNoPrice = !(cell.orderInfo || cell.positionInfo)
          const secondLine = cell.orderInfo
            ? `${(cell.orderInfo.orderPrice * 100).toFixed(1)} ${Math.round(cell.orderInfo.sizeMatched)}/${Math.round(cell.orderInfo.sizeTotal)}`
            : cell.orderInfoNo
              ? `${(cell.orderInfoNo.orderPrice * 100).toFixed(1)} ${Math.round(cell.orderInfoNo.sizeMatched)}/${Math.round(cell.orderInfoNo.sizeTotal)}`
              : cell.positionInfo
                ? `${(cell.positionInfo.avgPrice * 100).toFixed(1)} ${Math.round(cell.positionInfo.size)}`
                : cell.positionInfoNo
                  ? `${(cell.positionInfoNo.avgPrice * 100).toFixed(1)} ${Math.round(cell.positionInfoNo.size)}`
                  : null
          const textColor = hasOrder ? 'text-amber-300' : hasPosition ? 'text-emerald-300' : hasPositionNo ? 'text-red-300' : 'text-[#00ff6a]'
          const isSelected = selectedCellIds?.has(cell.id)

          const priceTextWithProb = cell.yesTokenId || cell.noTokenId ? (
            <span className="flex flex-col items-center gap-0">
              {showYesPrice && (
                <span className="flex items-center gap-0.5 leading-tight">
                  <span className="text-[#00ff6a] text-sm font-semibold">{yesPriceStr}</span>
                </span>
              )}
              {showYesPrice && showNoPrice && <span className="w-full border-b border-gray-500/50" style={{ minWidth: 16 }} />}
              {showNoPrice && (
                <span className="flex items-center gap-0.5 leading-tight">
                  <span className="text-red-400 text-sm font-semibold">{noPriceStr}</span>
                </span>
              )}
            </span>
          ) : '—'

          return (
            <div
              key={`${cell.id}-${ri}-${ci}`}
              className={cn(
                'pointer-events-none absolute flex flex-col items-center justify-center overflow-hidden rounded-none',
                (disabled || placeBet.isPending || !cell.yesTokenId) && 'opacity-60'
              )}
              style={{
                left: btnLeft,
                top: btnTop,
                width: btnW,
                height: btnH,
                ...overlayStyle,
                border: '0.5px solid rgba(148,163,184,0.35)',
                ...(isSelected && { boxShadow: '4px 0 0 0 #b87333' }),
              }}
            >
              <span className={cn('text-center text-xs font-bold', textColor)}>{priceTextWithProb}</span>
              {secondLine && <span className={cn('text-center text-[10px] font-medium', textColor)}>{secondLine}</span>}
            </div>
          )
        })
      )}
    </div>
  )
})
